import React, { useMemo, useState } from "react";
import { useAppDispatch } from "store";
import { Form, DateRangePicker, Input, HStack } from "rsuite";
import { TagFilter } from "@rsuite/icons";
import { formatDateToLocalISOString } from "utils";
import { toBackendUTCString, parseUTCFromUrl, toDisplayTZ, fromDisplayTZ } from "utils/time";
import styled from "styled-components";
import debounce from "lodash/debounce";

import FormControl from "components/base/FormControl";
import FormGroup from "components/base/FormGroup";
import Panel from "components/base/Panel";
import PrimaryButton from "components/Button/PrimaryButton";
import Icon from "components/base/Icon";

import { validationHasErrors } from "utils";
import { RawPacketFilters, rawPacketsFormModel } from "types/filterConfig";

type FormModelType = typeof rawPacketsFormModel;

type RawPacketsFiltersProps = {
    expanded?: boolean;
    model: FormModelType;
    loading: boolean;
    filters: Record<string, any>;
    formError: Record<string, string>;
    setFilters: React.Dispatch<React.SetStateAction<RawPacketFilters>>;
    setFormError: React.Dispatch<React.SetStateAction<Record<string, string>>>;
    setRefreshKey: React.Dispatch<React.SetStateAction<number | null>>;
    setSearchParams: (params: URLSearchParams) => void;
};


const RawPacketsFilters: React.FC<RawPacketsFiltersProps> = ({
    expanded,
    model,
    loading,
    filters,
    formError,
    setFilters,
    setFormError,
    setRefreshKey,
    setSearchParams
}) => {
    const [filtersState, setFiltersState] = useState(filters);

    const [timeRange, setTimeRange] = useState<{
        time_from: Date | null;
        time_to: Date | null;
    }>({
        time_from: parseUTCFromUrl(filters.time_from),
        time_to: parseUTCFromUrl(filters.time_to)
    });

    const filtersFormValue = useMemo(() => {
        return {
            time_range: timeRange.time_from && timeRange.time_to
                ? [timeRange.time_from, timeRange.time_to]
                : null,
            ip_src: filters.ip_src || "",
            ip_dst: filters.ip_dst || "",
            call_id: filters.call_id || "",
            method_code: filters.method_code || ""
        };
    }, [filters, timeRange]);

    const handleFilterChange = () => {
        const utcFrom = timeRange.time_from ? toBackendUTCString(timeRange.time_from) : "";
        const utcTo   = timeRange.time_to   ? toBackendUTCString(timeRange.time_to)   : "";

        const nextUrlFilters: RawPacketFilters = {
            ip_src: filtersState.ip_src || "",
            ip_dst: filtersState.ip_dst || "",
            call_id: filtersState.call_id || "",
            method_code: filtersState.method_code || "",
            time_from: utcFrom,
            time_to:   utcTo
        };

        const validation = model.check(nextUrlFilters);
        const newErrors: Record<string, string> = {};

        Object.entries(validation).forEach(([key, val]: any) => {
            if (val.hasError) {
                newErrors[key] = val.errorMessage;
            }
        });

        setFormError(newErrors);

        if (validationHasErrors(validation)) return;

        const next = new URLSearchParams(window.location.search);
        Object.entries(nextUrlFilters).forEach(([k, v]) => next.set(k, v || ""));
        setSearchParams(next);

        setFilters(nextUrlFilters);
        setRefreshKey(Date.now());
    };

    const pickerValue =
        timeRange.time_from && timeRange.time_to
            ? [toDisplayTZ(timeRange.time_from)!, toDisplayTZ(timeRange.time_to)!]
            : null;


    return (
        // <FiltersContainer>
        <Panel
            // header="Filters"
            expanded={expanded}
            noStyles
        >
            <Form
                model={model}
                formError={formError}
                formDefaultValue={filtersFormValue}
                onChange={debounce((value: any) => {
                    setFiltersState({
                        ip_src: value.ip_src || "",
                        ip_dst: value.ip_dst || "",
                        call_id: value.call_id || "",
                        method_code: value.method_code || ""
                    });
                }, 500)}
            >
                <HStack spacing={16} justifyContent="space-between" alignItems="center">
                    <FormGroup gap={8} horizontal>
                        <FormControl
                            disabled={loading}

                            as={DateRangePicker}
                            width={330}
                            value={pickerValue}
                            onChange={(val: [Date, Date] | null) => {
                                const timeFrom = val?.[0] ?? null;
                                const timeTo = val?.[1] ?? null;

                                const utcFrom = fromDisplayTZ(timeFrom);
                                const utcTo   = fromDisplayTZ(timeTo);

                                setTimeRange({ time_from: utcFrom, time_to: utcTo });
                            }}
                            onClean={() => {
                                setTimeRange({ time_from: null, time_to: null });
                            }}
                            showOneCalendar
                            size="md"
                            name="time_range"
                            placeholder="Select range"
                            format="dd/MM/yyyy HH:mm:ss"
                            cleanable={false}
                        />
                        <FormControl
                            disabled={loading}
                            width={115}
                            as={Input}
                            placeholder="Incoming IP..."
                            name="ip_src"
                            size="md"
                        />
                        <FormControl
                            disabled={loading}
                            width={115}
                            as={Input}
                            placeholder="Outgoing IP..."
                            name="ip_dst"
                            size="md"
                        />
                        <FormControl
                            disabled={loading}
                            width={200}
                            as={Input}
                            placeholder="Call ID..."
                            name="call_id"
                            size="md"
                        />
                        <FormControl
                            disabled={loading}
                            width={120}
                            as={Input}
                            placeholder="Method/Code..."
                            name="method_code"
                            size="md"
                        />
                    </FormGroup>
                    <PrimaryButton
                        disabled={loading}
                        onClick={() => {
                            handleFilterChange();
                        }}
                    >
                        <Icon as={TagFilter} />
                        <span>Show</span>
                    </PrimaryButton>
                </HStack>
            </Form>
        </Panel>
    );
};

export default RawPacketsFilters; 